import { z } from 'zod';
import { CompetenciaChave, DataISO, Id } from './comum.js';
import { Entregavel, EntregaveisVariaveis, PapelDocumento, TipoGuiaEntrega } from './entregaveis.js';
import { ComposicaoItem, EvidenciaPagamento } from './guia.js';
import { LeituraGuia } from './detalhe.js';

/**
 * "O que esta tarefa gera" com o que já chegou em cada vaga, e os cartões de confirmação de arquivos (ler → salvar).
 * Ver docs/guias-e-entregas.md, fatia 1b.
 */
/** mesmo piso e teto da entrega (Decimal(14,2) no banco; negativo não existe) */
const Valor = z.number().min(0).max(999_999_999_999);

export const SituacaoEntregavel = z.enum(['FALTA', 'PRONTA', 'A_VENCER', 'VENCE_HOJE', 'VENCIDA', 'PAGA']);
export type SituacaoEntregavel = z.infer<typeof SituacaoEntregavel>;

export const EntregavelEstado = Entregavel.extend({
  /** FIXO = vaga declarada; VARIAVEL = guia do grupo variável (DCTFWeb/Reinf); EXTRA = chegou fora da lista */
  origem: z.enum(['FIXO', 'VARIAVEL', 'EXTRA']),
  entregaId: Id.nullable(), anexoId: Id.nullable(),
  situacao: SituacaoEntregavel,
  publicada: z.boolean(), enviada: z.boolean(),
  /** da guia: "pagar até" ou vencimento */
  vencimento: DataISO.nullable(), valor: z.number().nullable(),
  /** da guia (fatia 2a): informada pelo cliente, confirmada, não encontrada… */
  pagamentoEvidencia: EvidenciaPagamento.optional(),
  /** o cliente pediu a guia atualizada */
  atualizadaPedidaEm: z.string().nullable().optional(),
});
export type EntregavelEstado = z.infer<typeof EntregavelEstado>;

export const ResumoEntregaveis = z.object({
  /** vagas fixas */ total: z.number().int(), preenchidos: z.number().int(),
  /** nomes das vagas obrigatórias ainda vazias */ faltam: z.array(z.string()),
  variaveis: EntregaveisVariaveis.nullable(),
  itens: z.array(EntregavelEstado),
});
export type ResumoEntregaveis = z.infer<typeof ResumoEntregaveis>;

/** Cartão proposto pela leitura de um arquivo (já anexado), antes de gravar. */
export const DocumentoLido = z.object({
  anexoId: Id, nome: z.string(), papel: PapelDocumento,
  confianca: z.enum(['ALTA', 'MEDIA', 'BAIXA', 'NENHUMA']),
  titulo: z.string().nullable(), tipoGuia: TipoGuiaEntrega.nullable(), codigoReceita: z.string().nullable(), competenciaChave: CompetenciaChave.nullable(),
  vencimento: DataISO.nullable(), pagarAte: DataISO.nullable(),
  valorTotal: z.number().nullable(), valorPrincipal: z.number().nullable(), valorMulta: z.number().nullable(), valorJuros: z.number().nullable(),
  linhaDigitavel: z.string().nullable(), numeroDocumento: z.string().nullable(), composicao: z.array(ComposicaoItem).nullable(),
  /** vaga sugerida pelo tipo e código (chave do entregável) */
  entregavelChave: z.string().nullable(),
  avisos: z.array(z.string()), leitura: LeituraGuia.nullable(),
});
export type DocumentoLido = z.infer<typeof DocumentoLido>;

/** O que a pessoa confirmou em cada cartão: salvar = registrar (guia ou recibo) ou titular (documento), e publicar se marcado. */
export const DocumentoSalvarItem = z.object({
  anexoId: Id, papel: PapelDocumento,
  titulo: z.string().trim().max(120).nullable().default(null),
  entregavelChave: z.string().trim().max(40).nullable().default(null),
  publicar: z.boolean().default(true),
  // guia
  tipoGuia: TipoGuiaEntrega.nullable().default(null), codigoReceita: z.string().trim().max(20).nullable().default(null), competenciaChave: CompetenciaChave.nullable().default(null),
  vencimento: DataISO.nullable().default(null), pagarAte: DataISO.nullable().default(null),
  valorTotal: Valor.nullable().default(null), valorPrincipal: Valor.nullable().default(null), valorMulta: Valor.nullable().default(null), valorJuros: Valor.nullable().default(null),
  linhaDigitavel: z.string().trim().max(80).nullable().default(null), codigoBarras: z.string().trim().max(60).nullable().default(null), numeroDocumento: z.string().trim().max(60).nullable().default(null),
  composicao: z.array(ComposicaoItem).nullable().default(null),
  /** reemissão: esta guia substitui aquela (a antiga sai do portal e dos lembretes; o link antigo avisa) */
  substituiEntregaId: Id.nullable().default(null),
  // recibo
  protocolo: z.string().trim().max(120).nullable().default(null), dataTransmissao: DataISO.nullable().default(null), retificadora: z.boolean().default(false),
});
export type DocumentoSalvarItem = z.infer<typeof DocumentoSalvarItem>;
export const DocumentosSalvarBody = z.object({
  itens: z.array(DocumentoSalvarItem).min(1).max(50),
  /** o que for publicado entra no envio ao cliente (junta com o que mais for salvo hoje; sai em minutos; dá para desfazer) */
  enviar: z.boolean().default(false),
  mensagem: z.string().trim().max(1000).nullable().default(null),
});
export type DocumentosSalvarBody = z.infer<typeof DocumentosSalvarBody>;
export const DocumentosSalvarResultado = z.object({
  guias: z.number().int(), recibos: z.number().int(), documentos: z.number().int(), publicados: z.number().int(), avisos: z.array(z.string()),
  /** o envio ao cliente em que o que foi salvo entrou (quando pedido) */
  envio: z.object({ remessaId: Id, enviarApos: z.string().nullable(), novos: z.number().int(), aviso: z.string().nullable(), /** sai agora (guia vence hoje): não há janela para desfazer antes */ urgente: z.boolean().optional() }).nullable().optional(),
});
export type DocumentosSalvarResultado = z.infer<typeof DocumentosSalvarResultado>;
